import React, { useState } from "react";
import ThemeButton from "./ThemeButton";
import { posts } from "../generated/posts";

interface HeaderProps {
  activePage: string;
  onNavigate: (page: string) => void;
  scrolled: boolean;
}

export default function Header({ activePage, onNavigate, scrolled }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleNavClick = (page: string) => {
    onNavigate(page);
    setIsMenuOpen(false);
  };

  const navItem = (page: string, label: string, active: boolean) => (
    <button
      onClick={() => onNavigate(page)}
      className={`
        relative px-1 py-1 font-mono text-xs tracking-[0.15em] uppercase transition-colors
        hover:text-Sapphire cursor-pointer
        ${active ? "text-Sapphire" : "text-Subtext0"}
      `}
    >
      {label}
      {active && (
        <div className="absolute left-0 -bottom-1 w-full h-px bg-Sapphire"></div>
      )}
    </button>
  );

  return (
    <>
      <header
        className={`
          fixed top-0 left-0 w-full z-50 bg-Base/80 backdrop-blur border-b hairline
          flex items-center justify-between px-6 md:px-12
          transition-all duration-300 ease-in-out
          ${scrolled ? "h-12" : "h-16"}
        `}
      >
        {/* Left: Brand */}
        <button
          onClick={() => onNavigate("home")}
          className={`font-display italic text-Text cursor-pointer transition-all duration-300 ${scrolled ? "text-lg" : "text-xl"}`}
        >
          Tadej Gungl
        </button>

        {/* Right: Desktop Navigation & Tools */}
        <div className="hidden md:flex items-center gap-8">
          <nav className="flex items-center gap-6">
            {navItem("home", "Portfolio", activePage === "home")}
            {navItem("cv", "CV", false)}
            {posts.length > 0 && navItem("blog", "Blog", activePage === "blog")}
            {navItem("contact", "Contact", false)}
          </nav>

          <ThemeButton />
        </div>

        {/* Mobile Burger Button */}
        <button
          onClick={toggleMenu}
          className="md:hidden flex flex-col justify-center items-center w-8 h-8 gap-1.5 z-50 focus:outline-none"
          aria-label="Toggle Menu"
        >
          <span
            className={`block w-6 h-px bg-Text transition-all duration-300 ${isMenuOpen ? "rotate-45 translate-y-[7px]" : ""}`}
          ></span>
          <span
            className={`block w-6 h-px bg-Text transition-all duration-300 ${isMenuOpen ? "opacity-0" : ""}`}
          ></span>
          <span
            className={`block w-6 h-px bg-Text transition-all duration-300 ${isMenuOpen ? "-rotate-45 -translate-y-[7px]" : ""}`}
          ></span>
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`
          fixed inset-0 bg-Base z-40 transition-transform duration-300 ease-in-out md:hidden flex flex-col items-center justify-center
          ${isMenuOpen ? "translate-y-0" : "-translate-y-full"}
        `}
      >
        <nav className="flex flex-col items-start gap-6 px-10 w-full max-w-sm">
          {[
            { page: "home", num: "01", label: "Portfolio" },
            { page: "cv", num: "02", label: "CV" },
            ...(posts.length > 0 ? [{ page: "blog", num: "03", label: "Blog" }] : []),
            { page: "contact", num: posts.length > 0 ? "04" : "03", label: "Contact" },
          ].map((item) => (
            <button
              key={item.page}
              onClick={() => handleNavClick(item.page)}
              className="group flex items-baseline gap-4"
            >
              <span className="font-mono text-xs text-Sapphire">{item.num}</span>
              <span
                className={`font-display font-light text-4xl transition-colors group-hover:text-Sapphire ${activePage === item.page ? "text-Sapphire italic" : "text-Text"}`}
              >
                {item.label}
              </span>
            </button>
          ))}

          <div className="w-full border-t hairline my-4"></div>

          <ThemeButton />
        </nav>
      </div>
    </>
  );
}
